import React from 'react';

import {assertAndContinue} from 'Asserts';
import {
  Collaborator,
  DataCollaborator,
  isDataPolicyActorCollaborator,
  isDataPolicyGroupCollaborator,
  Owner,
} from 'auth/PermissionConstants';
import {hasAllOrgsWithDisablePersonalOwnables} from 'orgs/DisablePersonalOwnablesUtil';
import {capwords, typeToPluralized} from 'util/NameUtil';

/**
 * Get all possible owners for new ownables.
 */
export function getOwnerChoices(): Array<Owner> {
  return window.bootstrap!.owner_choices;
}

export function shouldHideSelectOwnerField(): boolean {
  return hasAllOrgsWithDisablePersonalOwnables() && getOwnerChoices().length <= 1;
}

export enum OwnershipType {
  CREATOR = 'creator',
  USER = 'user',
  USER_AND_ORG = 'user_and_org',
}

/**
 * Return help message for SelectOwnerField.
 */
export function getSelectOwnerHelpMessage(
  ownableType: string,
  collaboratorsAllowed: boolean,
  ownershipType: OwnershipType
): JSX.Element {
  const ownableName = typeToPluralized(ownableType);

  const adminMessageForCollaboratorsAllowed = `Owners have administrative privileges to add and remove \
  collaborators and manage permissions.`;
  const adminMessageForCollaboratorsNotAllowed = `Owners have administrative privileges.`;

  const ownerMessageForOnlyCreatorOwned = `${capwords(ownableName)} are always owned by \
  their creator.`;
  const ownerMessageForOnlyUserOwned = `${capwords(ownableName)} can only be owned by a \
  user.`;
  const ownerMessageForOrgOrUserOwned = `${capwords(ownableName)} can be owned by a \
  user or an organization.`;

  const organizationAdminMessage = `When owned by an organization, the organization's admins are \
  granted administrative privileges to the ${ownableName}.`;

  const ownerMessageMap = {
    [OwnershipType.CREATOR]: ownerMessageForOnlyCreatorOwned,
    [OwnershipType.USER]: ownerMessageForOnlyUserOwned,
    [OwnershipType.USER_AND_ORG]: ownerMessageForOrgOrUserOwned,
  };

  const paragraph1 = (
    <p>
      {`${ownerMessageMap[ownershipType]}
      ${collaboratorsAllowed ? adminMessageForCollaboratorsAllowed : adminMessageForCollaboratorsNotAllowed}`}
    </p>
  );

  const paragraph2 = collaboratorsAllowed ? (
    <p>
      {organizationAdminMessage}
      {` To manage permissions for other members of the organization, use the `}
      <strong>Collaborators</strong>
      {' section.'}
    </p>
  ) : (
    <p>{organizationAdminMessage}</p>
  );

  const message =
    ownershipType !== OwnershipType.USER_AND_ORG ? (
      <div>{paragraph1}</div>
    ) : (
      <div>
        {paragraph1}
        {paragraph2}
      </div>
    );

  return message;
}
export function toServerCollaborators(collaborators: Array<Collaborator>): Array<DataCollaborator> {
  return collaborators
    .map((collaborator) => {
      if (isDataPolicyActorCollaborator(collaborator)) {
        return {
          id: collaborator.id,
          dataPolicyId: collaborator.dataPolicyId,
        };
      }
      if (isDataPolicyGroupCollaborator(collaborator)) {
        return {
          id: collaborator.id,
          adminDataPolicyId: collaborator.adminDataPolicyId,
          memberDataPolicyId: collaborator.memberDataPolicyId,
        };
      }
      assertAndContinue(false, `Unexpected collaborator type ${collaborator}`);
      return null;
    })
    .filter((collaborator: DataCollaborator | null): collaborator is DataCollaborator =>
      Boolean(collaborator)
    );
}
